import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Sparkles } from "lucide-react";
import { Reveal, Label, SectionTitle } from "@/components/Reveal";
import { IMAGES, COUNTERS, CATEGORIES, TESTIMONIALS, WHATSAPP } from "@/data/content";

const Hero = () => (
  <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero">
    <div className="absolute inset-0">
      <img src={IMAGES.heroOrnate} alt="Luxury salon" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-midnight via-midnight/85 to-midnight/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-midnight via-transparent to-midnight/60" />
    </div>
    <div className="absolute left-1/4 top-0 w-px h-full bg-gradient-to-b from-transparent via-gold/30 to-transparent animate-streak" />
    <div className="absolute right-1/3 top-0 w-px h-full bg-gradient-to-b from-transparent via-champagne/20 to-transparent animate-streak" style={{ animationDelay: "3s" }} />

    <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pt-24">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: "easeOut" }} className="max-w-3xl">
        <Label>Bangalore · HRBR Layout · Kalyan Nagar</Label>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none mt-6">
          Beauty Is An Art.<br /><span className="gold-shimmer italic">We Make It Extraordinary.</span>
        </h1>
        <p className="text-lg lg:text-xl text-white/75 mt-7 max-w-2xl leading-relaxed">
          Bangalore's luxury destination for Hair, Beauty, Makeup, Nails &amp; Professional Beauty Education.
        </p>
        <div className="flex flex-wrap gap-4 mt-10">
          <Link to="/book" className="btn-gold flex items-center gap-2" data-testid="hero-book">Book Appointment <ArrowRight size={18} /></Link>
          <Link to="/academy" className="btn-ghost" data-testid="hero-enroll">Enroll in Academy</Link>
          <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="hero-whatsapp">WhatsApp Now</a>
        </div>
      </motion.div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-20">
        {COUNTERS.map((c, i) => (
          <Reveal key={c.label} delay={i * 0.1}>
            <div className="glass rounded-lg p-6 text-center transition-colors duration-500">
              <div className="text-3xl lg:text-4xl gold-text font-serif">{c.value}</div>
              <div className="font-label text-[10px] text-white/60 mt-2">{c.label}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Categories = () => (
  <section className="py-24 lg:py-32 max-w-7xl mx-auto px-5 lg:px-8" data-testid="categories">
    <SectionTitle label="Signature Experiences" title="A World of Luxury, Curated for You" sub="From transformative hair artistry to certified professional education — everything under one opulent roof." />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {CATEGORIES.map((c, i) => (
        <Reveal key={c.title} delay={(i % 3) * 0.1}>
          <div className="group relative aspect-[4/5] overflow-hidden rounded-lg border border-gold/20" data-testid={`category-${c.title.toLowerCase()}`}>
            <img src={c.img} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/40 to-transparent" />
            <div className="absolute bottom-0 p-7">
              <h3 className="text-2xl">{c.title}</h3>
              <p className="text-white/70 text-sm mt-1">{c.desc}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const BeforeAfter = () => {
  const [pos, setPos] = React.useState(50);
  return (
    <section className="py-24 bg-royal/20 border-y border-gold/10" data-testid="before-after">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Label>Real Transformations</Label>
          <h2 className="text-4xl sm:text-5xl tracking-tight mt-4">See the Difference<br />We Create.</h2>
          <p className="text-white/70 mt-5 leading-relaxed">Slide to reveal the artistry behind every Spunky Scissors makeover — precision, colour and confidence in every finish.</p>
          <Link to="/gallery" className="btn-ghost inline-flex mt-8" data-testid="ba-gallery">Explore Gallery</Link>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-gold/30 select-none">
            <img src={IMAGES.hair2} alt="After" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
              <img src={IMAGES.salon2} alt="Before" className="absolute inset-0 w-full h-full object-cover" style={{ width: "100vw", maxWidth: "none" }} />
            </div>
            <div className="absolute top-0 bottom-0 w-0.5 bg-gold" style={{ left: `${pos}%` }} />
            <input type="range" min="0" max="100" value={pos} onChange={(e) => setPos(e.target.value)} data-testid="ba-slider"
              className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize" />
            <span className="absolute bottom-3 left-3 font-label text-[10px] bg-midnight/70 px-2 py-1 rounded">Before</span>
            <span className="absolute bottom-3 right-3 font-label text-[10px] bg-gold/90 text-midnight px-2 py-1 rounded">After</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const AcademyHighlight = () => (
  <section className="py-24 lg:py-32 max-w-7xl mx-auto px-5 lg:px-8" data-testid="academy-highlight">
    <div className="grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-gold/20">
          <img src={IMAGES.academy} alt="Academy" className="w-full h-full object-cover" />
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <Label>Spunky Scissors Academy</Label>
        <h2 className="text-4xl sm:text-5xl tracking-tight mt-4">Turn Your Passion<br />Into a Profession.</h2>
        <p className="text-white/70 mt-5 leading-relaxed">Government-recognised certification, hands-on training, EMI options and real placement assistance. Join 1000+ students who built thriving beauty careers.</p>
        <div className="flex gap-4 mt-8">
          <Link to="/academy" className="btn-gold flex items-center gap-2" data-testid="academy-apply">Apply Now <Sparkles size={16} /></Link>
          <Link to="/student-success" className="btn-ghost" data-testid="academy-success">Success Stories</Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const Reviews = () => (
  <section className="py-24 bg-royal/20 border-y border-gold/10" data-testid="home-reviews">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <SectionTitle label="Loved by Bangalore" title="5-Star Client Reviews" />
      <div className="grid md:grid-cols-3 gap-6">
        {TESTIMONIALS.slice(0, 3).map((t, i) => (
          <Reveal key={t.name} delay={i * 0.1}>
            <div className="glass rounded-lg p-8 h-full transition-colors duration-500">
              <div className="flex gap-1 text-gold mb-4">{[...Array(5)].map((_, k) => <Star key={k} size={16} fill="#D4AF37" />)}</div>
              <p className="text-white/80 leading-relaxed italic">"{t.text}"</p>
              <div className="mt-5"><p className="text-gold">{t.name}</p><p className="text-white/50 text-xs">{t.role}</p></div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CTA = () => (
  <section className="py-28 text-center max-w-4xl mx-auto px-5" data-testid="home-cta">
    <Reveal>
      <Label>Your Best Self Awaits</Label>
      <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5">Look Good. Feel Confident.<br /><span className="gold-text italic">Be Your Best.</span></h2>
      <div className="flex flex-wrap justify-center gap-4 mt-10">
        <Link to="/book" className="btn-gold" data-testid="cta-book">Book Appointment</Link>
        <Link to="/bridal" className="btn-ghost" data-testid="cta-bridal">Explore Bridal Studio</Link>
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  return (<><Hero /><Categories /><BeforeAfter /><AcademyHighlight /><Reviews /><CTA /></>);
}
